// Geometry for placing the customer e-signature onto the LSA PDF.
//
// The signature is auto-anchored to the {account_signature} text marker on the
// signature line (see SignContract.jsx). Historically it was scaled by
// `scale * 0.3` and centred on the marker baseline with no upper bound, so a
// hand-drawn 200x200 signature became ~60pt tall (and larger on mobile, where
// `scale` > 1) and its halves spilled over the lines of text above and below —
// the reported "signature covers the text" bug on the LSA's last page.
//
// We keep the historical base scale and horizontal anchor, but cap the result
// to a small, bounded slot so the signature stays legible yet can never cover
// the lines of text above and below the signature marker.

export const SIGNATURE_SCALE_FACTOR = 0.3
// Cap the signature to ~1.5 text lines: large enough to read, small enough that
// (centred on the marker baseline) it stays clear of the neighbouring lines.
export const MAX_SIGNATURE_HEIGHT = 22 // pt
export const SIGNATURE_SLOT_WIDTH = 98 // pt
export const SIGNATURE_X_OFFSET = 98 // pt — left edge sits 98pt left of the marker's right edge

/**
 * Compute the pdf-lib drawImage box for a signature.
 *
 * pdf-lib anchors an image by its bottom-left corner, so the returned `y` is the
 * image's bottom edge. The box is centred on the marker baseline and bounded by
 * MAX_SIGNATURE_HEIGHT / SIGNATURE_SLOT_WIDTH; aspect ratio is preserved and the
 * signature is never enlarged beyond its historical `scale * 0.3` size.
 *
 * @param {object} p
 * @param {number} p.markerX       marker x (PDF units, from the text item transform)
 * @param {number} p.markerY       marker baseline y (PDF units)
 * @param {number} p.markerWidth   width of the {account_signature} marker text
 * @param {number} p.imageWidth    natural width of the embedded signature PNG
 * @param {number} p.imageHeight   natural height of the embedded signature PNG
 * @param {number} p.scale         page render scale (originalWidth / renderedWidth)
 * @returns {{x: number, y: number, width: number, height: number}}
 */
export function computeSignaturePlacement({
  markerX,
  markerY,
  markerWidth,
  imageWidth,
  imageHeight,
  scale,
}) {
  const baseScale = scale * SIGNATURE_SCALE_FACTOR
  // Shrink to fit the reserved slot when the base scale would overflow it, but
  // never upscale a signature that is already smaller. Aspect ratio is kept by
  // applying a single uniform scale factor.
  const fitScale = Math.min(
    baseScale,
    MAX_SIGNATURE_HEIGHT / imageHeight,
    SIGNATURE_SLOT_WIDTH / imageWidth
  )

  const width = imageWidth * fitScale
  const height = imageHeight * fitScale

  return {
    x: markerX + markerWidth - SIGNATURE_X_OFFSET,
    y: markerY - height / 2,
    width,
    height,
  }
}
